import React, { useState } from "react";

import {
  NO_VERDICT_LABEL,
  checkStateLabel,
  displayName,
  isAnnotationOnlyCriterion,
} from "manager/components/vero1/Vero1ReviewPanel/formatItemValue";
import { hasResultMoved } from "manager/components/vero1/Vero1ReviewPanel/itemState";
import type {
  Vero1ChangedItem,
  Vero1ChangedItems as Vero1ChangedItemsData,
} from "shared/interfaces/api/vero1";

import {
  ActionMarker,
  ChangedRow,
  ChangedRule,
  ChangedList,
  Chevron,
  Findings,
  ActionNeeded,
  ItemName,
  RationaleText,
  ReasonLabel,
  RowApplicant,
  RowDetail,
  RowHeader,
  WhyLineList,
  Rule,
  RuleHairline,
  RuleHeader,
  RuleLabel,
  SectionCount,
  StripLayout,
  ValuePill,
  ViewDetails,
} from "./styled";

interface Props {
  data: Vero1ChangedItemsData;
  /* The amended-by-VERO1 disclosure (Vero1LumenAmendedRow, bare) — spec nests
     it under the rationale, inside the same rule. Self-nulls for leasing. */
  amendedRow?: React.ReactNode;
  /* Screening facts rendered as the review's own reasoning under the
     rationale (2026-08-28 ruling) — plain lines, no source attribution. */
  whyLines?: string[];
  /* View details opens the deal review tab (PRODUCT-2060); the button stays
     inert when the caller has no deal to route to. */
  onViewDetails?: () => void;
}

function rowVerdict(item: Vero1ChangedItem): React.ReactNode {
  if (isAnnotationOnlyCriterion(item.criterionKey)) {
    const state = checkStateLabel(item.value);
    return state ? <ValuePill>{state}</ValuePill> : null;
  }
  if (item.resultMoved ?? hasResultMoved(item)) {
    return (
      <ValuePill>
        <span className="was">
          {displayName(item.originalName ?? undefined) ?? NO_VERDICT_LABEL}
        </span>
        <span className="arrow">→</span>
        {/* AC 4: leasing never reads the words Pass / Warn / Fail — a null
            name means the client configured none, so there is no verdict */}
        <span>
          {displayName(item.overrideName ?? undefined) ?? NO_VERDICT_LABEL}
        </span>
      </ValuePill>
    );
  }
  const standing = displayName(
    item.overrideName ?? item.originalName ?? undefined
  );
  return standing ? <ValuePill>{standing}</ValuePill> : null;
}

const RowChevron = ({ open }: { open: boolean }) => (
  <Chevron $open={open} aria-hidden>
    <svg
      width="11"
      height="11"
      viewBox="0 0 24 24"
      fill="none"
      strokeWidth="2.4"
      strokeLinecap="round"
    >
      <path d="M9 18l6-6-6-6" />
    </svg>
  </Chevron>
);

function ChangedItemRow({ item }: { item: Vero1ChangedItem }) {
  const [open, setOpen] = useState(false);
  const hasAction = Boolean(item.actionForLeasing);
  // a row is only expandable when it carries findings or an action to reveal
  const expandable = Boolean(item.findings || hasAction);

  return (
    <ChangedRow>
      <RowHeader
        as={expandable ? "button" : "div"}
        type={expandable ? "button" : undefined}
        $expandable={expandable}
        onClick={expandable ? () => setOpen((v) => !v) : undefined}
        aria-expanded={expandable ? open : undefined}
      >
        <RowChevron open={open} />
        <ItemName $open={open}>
          {item.label}
          {item.applicant && <RowApplicant> · {item.applicant}</RowApplicant>}
        </ItemName>
        {hasAction && <ActionMarker>Action</ActionMarker>}
        {rowVerdict(item)}
      </RowHeader>
      {open && (
        <RowDetail>
          {/* the catalog's readable reason label — the categorical why for the
              row; the raw code never reaches the client (backend D13) */}
          {item.reason && <ReasonLabel>{item.reason}</ReasonLabel>}
          {item.findings && <Findings>{item.findings}</Findings>}
          {hasAction && (
            <ActionNeeded>
              <b>Action needed —</b> {item.actionForLeasing}
            </ActionNeeded>
          )}
        </RowDetail>
      )}
    </ChangedRow>
  );
}

export function Vero1ChangedItems({
  data,
  amendedRow,
  whyLines,
  onViewDetails,
}: Props) {
  const count = data.items.length;
  const actionCount = data.items.filter((i) => i.actionForLeasing).length;
  const hasWhyLines = Boolean(whyLines?.length);
  if (!data.rationale && !hasWhyLines && count === 0 && amendedRow == null)
    return null;
  return (
    <StripLayout>
      {(data.rationale || hasWhyLines || amendedRow != null) && (
        <Rule>
          <RuleHeader>
            <RuleLabel>Why this recommendation</RuleLabel>
            <RuleHairline />
            {/* View details opens the full review on the deal review tab */}
            <ViewDetails
              type="button"
              aria-disabled={onViewDetails ? undefined : true}
              onClick={onViewDetails}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#B39BEC"
                strokeWidth="2"
              >
                <rect x="3" y="4" width="18" height="16" rx="2" />
                <path d="M3 9h18M9 9v11" />
              </svg>
              View details
            </ViewDetails>
          </RuleHeader>
          {data.rationale && <RationaleText>{data.rationale}</RationaleText>}
          {hasWhyLines && (
            <WhyLineList>
              {whyLines!.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </WhyLineList>
          )}
          {amendedRow}
        </Rule>
      )}
      {count > 0 && (
        <ChangedRule>
          <RuleHeader>
            <RuleLabel>What VERO1 changed</RuleLabel>
            <RuleHairline />
            <SectionCount>
              {count} {count === 1 ? "item" : "items"}
              {actionCount > 0 &&
                ` · ${actionCount} need${actionCount === 1 ? "s" : ""} action`}
            </SectionCount>
          </RuleHeader>
          <ChangedList>
            {data.items.map((item, i) => (
              <ChangedItemRow key={`${item.label}-${i}`} item={item} />
            ))}
          </ChangedList>
        </ChangedRule>
      )}
    </StripLayout>
  );
}
